import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/layouts/AppLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { api } from '@/lib/api';
import type { License, QueryFilters, TableColumn } from '@/types';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import ExportModal from '@/components/ExportModal';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  ChartBarIcon,
  MapIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

interface DataStats {
  totalLicenses: number;
  activeCount: number;
  inactiveCount: number;
  topCities: Array<{ city: string; count: number }>;
  topLicenseTypes: Array<{ type: string; count: number }>;
}

interface FullDataFilters {
  searchTerm: string;
  selectedCity: string;
  selectedLicenseType: string;
  selectedStatus: string;
  selectedEnrichment: string;
  selectedRating: string;
  selectedContact: string;
  excludeChains: boolean;
}

export const FullDataPage: React.FC = () => {
  // Load filters from localStorage
  const loadFiltersFromStorage = (): FullDataFilters => {
    try {
      const stored = localStorage.getItem('fullDataFilters');
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          searchTerm: parsed.searchTerm || '',
          selectedCity: parsed.selectedCity || '',
          selectedLicenseType: parsed.selectedLicenseType || '',
          selectedStatus: parsed.selectedStatus || '',
          selectedEnrichment: parsed.selectedEnrichment || '',
          selectedRating: parsed.selectedRating || '',
          selectedContact: parsed.selectedContact || '',
          excludeChains: Boolean(parsed.excludeChains),
        };
      }
    } catch (error) {
      console.warn('Failed to load full data filters from localStorage:', error);
    }
    return {
      searchTerm: '',
      selectedCity: '',
      selectedLicenseType: '',
      selectedStatus: '',
      selectedEnrichment: '',
      selectedRating: '',
      selectedContact: '',
      excludeChains: false,
    };
  };

  const savedFilters = loadFiltersFromStorage();

  const [filters] = useState<QueryFilters>({
    limit: 50,
    offset: 0,
  });
  const [results, setResults] = useState<License[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm);
  const [selectedCity, setSelectedCity] = useState(savedFilters.selectedCity);
  const [selectedLicenseType, setSelectedLicenseType] = useState(savedFilters.selectedLicenseType);
  const [selectedStatus, setSelectedStatus] = useState(savedFilters.selectedStatus);
  const [selectedEnrichment, setSelectedEnrichment] = useState(savedFilters.selectedEnrichment);
  const [selectedRating, setSelectedRating] = useState(savedFilters.selectedRating);
  const [selectedContact, setSelectedContact] = useState(savedFilters.selectedContact);
  const [excludeChains, setExcludeChains] = useState(savedFilters.excludeChains);
  const [stats, setStats] = useState<DataStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadResult, setDownloadResult] = useState<string | null>(null);

  const columns: TableColumn<License>[] = [
    {
      key: 'business_name',
      label: 'Business Name',
      sortable: true,
      render: (value: any, license: License) => (
        <div className="font-medium text-gray-900">
          {license.business_name}
          {license.dba_name && license.dba_name !== license.business_name && (
            <div className="text-sm text-gray-500">DBA: {license.dba_name}</div>
          )}
        </div>
      ),
    },
    {
      key: 'mailing_address',
      label: 'Mailing Address',
      sortable: false,
      render: (value: any, license: License) => {
        // Debug: Log the first few records to see what address fields are available
        if (Math.random() < 0.1) { // Log ~10% of records to avoid spam
          console.log('🏠 Address debug for license:', license.license_number, {
            mailing_address: license.mailing_address,
            premise_address: license.premise_address,
            mailing_city: license.mailing_city,
            premise_city: license.premise_city,
            mailing_state: license.mailing_state,
            premise_state: license.premise_state,
            allFields: Object.keys(license).filter(key => key.includes('address') || key.includes('city') || key.includes('state'))
          });
        }
        
        // Prioritize mailing address, fall back to premise address
        const address = license.mailing_address || license.premise_address;
        const city = license.mailing_city || license.premise_city;
        const state = license.mailing_state || license.premise_state;
        const zip = license.mailing_zip || license.premise_zip;
        
        return (
          <div className="text-sm">
            <div className="font-medium">{address || 'Address not available'}</div>
            <div className="text-gray-500">
              {city && state ? `${city}, ${state} ${zip || ''}`.trim() : 'City/State not available'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'license_type',
      label: 'License Type',
      sortable: true,
      render: (value: any, license: License) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          license.license_type?.includes('OFF-SALE') 
            ? 'bg-purple-100 text-purple-800'
            : license.license_type?.includes('ON-SALE')
            ? 'bg-orange-100 text-orange-800'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {license.license_type}
        </span>
      ),
    },
    {
      key: 'phone_number',
      label: 'Phone Number',
      sortable: false,
      render: (value: any, license: License) => (
        <div className="text-sm">
          {license.google_phone || license.phone_number ? (
            <div className="text-gray-900">
              {license.google_phone || license.phone_number}
              {license.google_phone && (
                <div className="text-xs text-green-600 mt-1">✓ Google Enriched</div>
              )}
            </div>
          ) : (
            <span className="text-gray-400">No phone</span>
          )}
        </div>
      ),
    },
    {
      key: 'license_type_status',
      label: 'Status',
      sortable: true,
      render: (value: any, license: License) => (
        <div>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            license.license_type_status === 'ACTIVE' 
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {license.license_type_status}
          </span>
          {license.enriched_at && (
            <div className="text-xs text-green-600 mt-1">✓ Enriched</div>
          )}
        </div>
      ),
    },
  ];

  // Load initial data and stats on component mount
  useEffect(() => {
    loadStats();
    searchData();
  }, []);

  // Reload data when currentPage changes
  useEffect(() => {
    if (currentPage > 1) { // Don't reload on initial mount (page 1)
      searchData();
    }
  }, [currentPage]);

  // Load data statistics
  const loadStats = async () => {
    try {
      // For now, we'll calculate basic stats from the search results
      // In a real implementation, you'd have a dedicated stats endpoint
      const basicQuery: QueryFilters = { limit: 1, offset: 0 };
      const response = await api.queryData(basicQuery);
      
      setStats({
        totalLicenses: response.total,
        activeCount: Math.floor(response.total * 0.85), // Estimate
        inactiveCount: Math.floor(response.total * 0.15), // Estimate
          topCities: [
            { city: 'Los Angeles', count: Math.floor(response.total * 0.15) },
            { city: 'San Francisco', count: Math.floor(response.total * 0.12) },
            { city: 'San Diego', count: Math.floor(response.total * 0.08) },
            { city: 'Sacramento', count: Math.floor(response.total * 0.06) },
            { city: 'Oakland', count: Math.floor(response.total * 0.05) },
          ],
          topLicenseTypes: [
            { type: 'OFF-SALE GENERAL', count: Math.floor(response.total * 0.25) },
            { type: 'ON-SALE GENERAL', count: Math.floor(response.total * 0.20) },
            { type: 'OFF-SALE BEER AND WINE', count: Math.floor(response.total * 0.15) },
            { type: 'ON-SALE BEER AND WINE', count: Math.floor(response.total * 0.12) },
          ],
        });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Search with specific filters (used by preset buttons)
  const searchWithFilters = async (overrideFilters: {
    licenseType?: string;
    status?: string;
    enrichment?: string;
    searchTerm?: string;
    city?: string;
  } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const pageSize = 50;
      const searchFilters: QueryFilters = {
        limit: pageSize,
        offset: 0, // Always start from page 1 for new searches
      };

      // Use override filters or current state
      const effectiveSearchTerm = overrideFilters.searchTerm ?? searchTerm;
      const effectiveCity = overrideFilters.city ?? selectedCity;
      const effectiveLicenseType = overrideFilters.licenseType ?? selectedLicenseType;

      if (effectiveSearchTerm) {
        searchFilters.business_name = effectiveSearchTerm;
      }
      if (effectiveCity) {
        searchFilters.cities = [effectiveCity];
      }
      if (effectiveLicenseType) {
        searchFilters.license_types = [effectiveLicenseType];
      }
      if (excludeChains) {
        searchFilters.exclude_chains = true;
      }

      const response = await api.queryData(searchFilters);
      
      setResults(response.licenses);
      setTotalResults(response.total);
      setCurrentPage(1); // Reset to page 1 for new searches
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to load data. Please try again.');
      setResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // Search and filter data
  const searchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const pageSize = 50;
      const searchFilters: QueryFilters = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (searchTerm) {
        searchFilters.business_name = searchTerm;
      }
      if (selectedCity) {
        searchFilters.cities = [selectedCity];
      }
      if (selectedLicenseType) {
        searchFilters.license_types = [selectedLicenseType];
      }
      if (excludeChains) {
        searchFilters.exclude_chains = true;
      }

      const response = await api.queryData(searchFilters);
      
      setResults(response.licenses);
      setTotalResults(response.total);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to load data. Please try again.');
      setResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // Save filters to localStorage
  const saveFiltersToStorage = (filters: FullDataFilters) => {
    try {
      localStorage.setItem('fullDataFilters', JSON.stringify(filters));
    } catch (error) {
      console.warn('Failed to save full data filters to localStorage:', error);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Note: searchData will be called by useEffect when currentPage changes
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    // Save current filters to localStorage
    saveFiltersToStorage({
      searchTerm,
      selectedCity,
      selectedLicenseType,
      selectedStatus,
      selectedEnrichment,
      selectedRating,
      selectedContact,
      excludeChains,
    });
    searchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters: FullDataFilters = {
      searchTerm: '',
      selectedCity: '',
      selectedLicenseType: '',
      selectedStatus: '',
      selectedEnrichment: '',
      selectedRating: '',
      selectedContact: '',
      excludeChains: false,
    };
    
    setSearchTerm('');
    setSelectedCity('');
    setSelectedLicenseType('');
    setSelectedStatus('');
    setSelectedEnrichment('');
    setSelectedRating('');
    setSelectedContact('');
    setExcludeChains(false);
    setCurrentPage(1);
    
    // Save cleared filters to localStorage
    saveFiltersToStorage(clearedFilters);
    searchData(); // Reload data with cleared filters
  };

  // Handle export
  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportWithFields = async (selectedFields: string[], exportFilters: QueryFilters) => {
    try {
      setDownloading(true);
      setDownloadResult(null);
      
      console.log('📥 Starting CSV download with selected fields:', selectedFields);
      console.log('📥 Export filters:', exportFilters);
      
      const queryFilters: QueryFilters = {
        business_name: searchTerm || undefined,
        cities: selectedCity ? [selectedCity] : undefined,
        license_types: selectedLicenseType ? [selectedLicenseType] : undefined,
        exclude_chains: excludeChains || undefined,
      };

      console.log('📥 Final query filters for CSV export:', queryFilters);

      // Call the CSV export API with selected fields
      const blob = await api.exportFilteredCSV(queryFilters, selectedFields);
      
      // Generate filename with current date and filter info
      const today = new Date().toISOString().split('T')[0];
      const cityFilter = selectedCity ? `_${selectedCity.toLowerCase().replace(/\s+/g, '_')}` : '';
      const licenseTypeFilter = selectedLicenseType ? `_${selectedLicenseType.toLowerCase().replace(/\s+/g, '_')}` : '';
      const filename = `full_data${cityFilter}${licenseTypeFilter}_${today}.csv`;
      
      // Use the File System Access API if available, otherwise fall back to download link
      if ('showSaveFilePicker' in window) {
        try {
          // Modern browsers - show Save As dialog
          const fileHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'CSV files',
              accept: { 'text/csv': ['.csv'] }
            }]
          });
          
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          console.log('🎉 CSV saved successfully via File System Access API');
          setDownloadResult(`Successfully saved ${totalResults} records to ${fileHandle.name}`);
        } catch (error: any) {
          if (error.name === 'AbortError') {
            // User cancelled the save dialog
            console.log('📝 User cancelled save dialog');
            setDownloadResult('Download cancelled by user');
            setTimeout(() => setDownloadResult(null), 3000);
            return;
          }
          throw error; // Re-throw other errors to fall back to download link
        }
      } else {
        // Fallback for older browsers - automatic download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        
        // Make the link invisible and add to DOM
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
        }, 100);
        
        console.log('🎉 CSV download completed via fallback method');
        setDownloadResult(`Successfully downloaded ${totalResults} records to ${filename}`);
      }
      
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => setDownloadResult(null), 5000);
      
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setDownloadResult(`Error: ${error instanceof Error ? error.message : 'Failed to download CSV'}`);
      
      // Auto-dismiss error message after 8 seconds
      setTimeout(() => setDownloadResult(null), 8000);
    } finally {
      setDownloading(false);
    }
  };



  // Reload when page changes
  useEffect(() => {
    searchData();
  }, [currentPage]);

  const pageSize = 50;
  const totalPages = Math.ceil(totalResults / pageSize);

  return (
    <AppLayout currentPath="/full-data">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Full Data
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Complete California ABC license database with advanced filtering and custom query capabilities.
            </p>
          </div>
          <Button
            onClick={() => { loadStats(); searchData(); }}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Licenses</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.totalLicenses.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Active Licenses</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stats.activeCount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <MapIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Top City</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {stats.topCities[0]?.city || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {stats.topCities[0]?.count.toLocaleString()} licenses
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FunnelIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Top License Type</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {stats.topLicenseTypes[0]?.type || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {stats.topLicenseTypes[0]?.count.toLocaleString()} licenses
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Business Names
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter business name..."
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <Select
                options={[
                  { value: '', label: 'All Cities' },
                  { value: 'Los Angeles', label: 'Los Angeles' },
                  { value: 'San Francisco', label: 'San Francisco' },
                  { value: 'San Diego', label: 'San Diego' },
                  { value: 'Sacramento', label: 'Sacramento' },
                  { value: 'Oakland', label: 'Oakland' },
                  { value: 'Fresno', label: 'Fresno' },
                ]}
                value={selectedCity}
                onChange={(value) => setSelectedCity(String(value))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                License Type
              </label>
              <Select
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'insurance-target', label: ' Insurance Targets (Types 20 & 21)' },
                  { value: '21', label: 'Type 21 - Off-Sale General (Full Liquor)' },
                  { value: '20', label: 'Type 20 - Off-Sale Beer & Wine (Markets)' },
                  { value: 'exclude-chains', label: ' Exclude Chains & Restaurants' },
                ]}
                value={selectedLicenseType}
                onChange={(value) => setSelectedLicenseType(String(value))}
              />
            </div>
          </div>
          
          {/* Quick Preset Buttons */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-sm font-medium text-gray-700 mr-3">Insurance Targeting Presets:</span>
                <Button
                  onClick={async () => {
                    // Update state for independent liquor stores
                    setSelectedLicenseType('insurance-target');
                    setSelectedStatus('ACTIVE');
                    setExcludeChains(true);
                    // Search with the new filters immediately
                    await searchWithFilters({
                      licenseType: 'insurance-target',
                      status: 'ACTIVE'
                    });
                  }}
                  className="mr-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm"
                >
                  Independent Liquor Stores (22K)
                </Button>
                <Button
                  onClick={async () => {
                    // Update state for Type 21 only
                    setSelectedLicenseType('21');
                    setSelectedStatus('ACTIVE');
                    setExcludeChains(true);
                    // Search with the new filters immediately
                    await searchWithFilters({
                      licenseType: '21',
                      status: 'ACTIVE'
                    });
                  }}
                  className="mr-2 bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-sm"
                >
                  Full Liquor Only (10.5K)
                </Button>
                <Button
                  onClick={async () => {
                    // Update state for Type 20 only
                    setSelectedLicenseType('20');
                    setSelectedStatus('ACTIVE');
                    setExcludeChains(true);
                    // Search with the new filters immediately
                    await searchWithFilters({
                      licenseType: '20',
                      status: 'ACTIVE'
                    });
                  }}
                  className="mr-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 text-sm"
                >
                  Beer & Wine Markets (11.8K)
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              These presets automatically exclude chain stores and focus on independent businesses perfect for insurance targeting.
            </p>
          </div>
          
          {/* Advanced Filters */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center mb-3">
              <FunnelIcon className="h-4 w-4 text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-700">Advanced Filters for Insurance Targeting</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Status
                </label>
                <Select
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'ACTIVE', label: 'Active Only' },
                    { value: 'INACTIVE', label: 'Inactive Only' },
                  ]}
                  value={selectedStatus}
                  onChange={(value) => setSelectedStatus(String(value))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enrichment Status
                </label>
                <Select
                  options={[
                    { value: '', label: 'All Businesses' },
                    { value: 'enriched', label: 'With Google Data' },
                    { value: 'not_enriched', label: 'Without Google Data' },
                  ]}
                  value={selectedEnrichment}
                  onChange={(value) => setSelectedEnrichment(String(value))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Rating
                </label>
                <Select
                  options={[
                    { value: '', label: 'Any Rating' },
                    { value: '4+', label: '4+ Stars' },
                    { value: '3+', label: '3+ Stars' },
                    { value: 'no_rating', label: 'No Rating' },
                  ]}
                  value={selectedRating}
                  onChange={(value) => setSelectedRating(String(value))}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Info
                </label>
                <Select
                  options={[
                    { value: '', label: 'All Businesses' },
                    { value: 'has_phone', label: 'Has Phone Number' },
                    { value: 'has_website', label: 'Has Website' },
                  ]}
                  value={selectedContact}
                  onChange={(value) => setSelectedContact(String(value))}
                />
              </div>
            </div>
            
            {/* Chain Exclusion Filter */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center">
                <input
                  id="exclude-chains"
                  type="checkbox"
                  checked={excludeChains}
                  onChange={(e) => setExcludeChains(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="exclude-chains" className="ml-2 text-sm font-medium text-gray-700">
                  Exclude Chain Stores (Focus on Independent Businesses)
                </label>
                {excludeChains && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Independent Only
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, filters out major chains like 7-Eleven, Shell, Chevron, Walmart, etc. Perfect for insurance targeting.
              </p>
            </div>
          </div>
          
          <div className="flex space-x-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <MagnifyingGlassIcon className="-ml-1 mr-2 h-4 w-4" />
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
            <button
              onClick={async () => {
                if (!excludeChains || (!selectedLicenseType || !['20', '21', 'insurance-target'].includes(selectedLicenseType))) {
                  alert('Please use one of the Insurance Targeting presets first to focus on independent liquor stores.');
                  return;
                }
                
                try {
                  // Use small batch size for testing (10 records)
                  const response = await api.triggerEnrichment({
                    batchSize: 10
                  });
                  
                  if (response.success) {
                    alert(` Test enrichment job queued successfully!\n\nJob ID: ${response.data?.jobId}\nBatch Size: 10 records\n\nCheck the Admin > Job Monitoring page to track progress.`);
                  } else {
                    alert(' Failed to queue enrichment job: ' + response.error);
                  }
                } catch (error) {
                  console.error('Enrichment error:', error);
                  alert('Failed to queue enrichment job. Please try again.');
                }
              }}
              className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 mr-2"
            >
              <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
              Test Enrichment (10 records)
            </button>
            <button
              onClick={handleExport}
              disabled={downloading || totalResults === 0}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentArrowDownIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
              {downloading ? 'Downloading...' : `Download ${totalResults} Records`}
            </button>
          </div>
        </div>
        
        <div className="text-sm text-gray-500">
          {totalResults > 0 && (
            <>Showing {((currentPage - 1) * 50) + 1}-{Math.min(currentPage * 50, totalResults)} of {totalResults.toLocaleString()} results</>
          )}
        </div>
        

        {/* Results */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <ErrorBoundary fallback={
            <div className="p-6 text-center text-red-600">
              <p>Error loading table. Please check the console for details.</p>
            </div>
          }>
            <Table
              columns={columns}
              data={results}
              loading={loading}
              emptyMessage="No licenses found. Try adjusting your search criteria."
            />
          </ErrorBoundary>
          
          {/* Results count and Pagination */}
          {totalResults > 0 && (() => {
            const pageSize = 50;
            const totalPages = Math.ceil(totalResults / pageSize);
            const startResult = ((currentPage - 1) * pageSize) + 1;
            const endResult = Math.min(currentPage * pageSize, totalResults);
            
            return (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {startResult} to {endResult} of {totalResults.toLocaleString()} results
                  </div>
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Download Result */}
        {downloadResult && (
          <div className={`mt-6 border rounded-lg p-4 ${
            downloadResult.startsWith('Error') 
              ? 'bg-red-50 border-red-200' 
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {downloadResult.startsWith('Error') ? (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  downloadResult.startsWith('Error') 
                    ? 'text-red-800' 
                    : 'text-green-800'
                }`}>
                  {downloadResult.startsWith('Error') ? 'Download Error' : 'Download Complete'}
                </p>
                <p className={`text-sm ${
                  downloadResult.startsWith('Error') 
                    ? 'text-red-700' 
                    : 'text-green-700'
                }`}>
                  {downloadResult}
                </p>
              </div>
              <div className="ml-auto">
                <button
                  type="button"
                  onClick={() => setDownloadResult(null)}
                  className={`text-sm font-medium ${
                    downloadResult.startsWith('Error') 
                      ? 'text-red-600 hover:text-red-500' 
                      : 'text-green-600 hover:text-green-500'
                  }`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExportWithFields}
          filters={{
            business_name: searchTerm || undefined,
            cities: selectedCity ? [selectedCity] : undefined,
            license_types: selectedLicenseType ? [selectedLicenseType] : undefined,
            exclude_chains: excludeChains || undefined,
          }}
          totalCount={totalResults}
          isExporting={downloading}
        />
      </div>
    </AppLayout>
  );
};

export default FullDataPage;
